import React, { useState, useEffect, useRef, useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { GlobalContext } from '../../../../utils/globalState'
import { colors } from '../../../../themes/colors'
import Alert from '../../../common/Alert'
import styles from './styles.module.css'

// component function
export default function DropZone(props) {

	const [globalState, setGlobalState] = useContext(GlobalContext)	// eslint-disable-line no-unused-vars
	const [highlight, setHighlight] = useState()
	const [file, setFile] = useState(null)
	const [error, setError] = useState()
	const [playing, setPlaying] = useState(true)
	const location = useLocation()
	const fileInputRef = useRef()
	const previewRef = useRef()
	const maxMb = globalState.userData?.organization?.subscriptionPlan?.config?.Max_Mb_per_film ? globalState.userData.organization.subscriptionPlan.config.Max_Mb_per_film : 1000
	const isTouchDevice = ('ontouchstart' in window) || ( navigator.maxTouchPoints > 0 ) || ( navigator.msMaxTouchPoints > 0 )
	const [alertTask, setAlertTask] = useState()
	const alertObjectRef = useRef({ type:'confirm', title:'You’re about to analyze a 4K or UHD video', message:'Flowsam supports large video sizes, but processing will take some extra minutes due to the higher pixel count. A large video will not improve the quality of the analysis, so we usually recommend analyzing HD or Full HD videos.', actionLabel:'I’ll use a smaller video', cancelLabel:'Ok, I don’t mind waiting' })

	// alert setup
	const alert = alertObjectRef.current !== null && alertTask !== undefined && <Alert type={alertObjectRef.current.type} title={alertObjectRef.current.title} message={alertObjectRef.current.message} cancelLabel={alertObjectRef.current.cancelLabel} actionLabel={alertObjectRef.current.actionLabel} action={alertTask} />

	// check if file object is added in location (comes from dashboard)
	useEffect(() => {
		location.state?.type && validateFile(location.state)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[location.state])

	// selected file thumbnail
	useEffect(() => {
		if (file === null || file === undefined) return
		var videoNode = document.querySelector('video')
		if (file.type?.indexOf('video/') === 0) {
			try {
				previewRef.current.style.backgroundImage = 'none'
				videoNode.src = URL.createObjectURL(file)
				setPlaying(true)
			} catch (error) {
				console.error(error);
			}
		} else if (file.type?.indexOf('image/') === 0) {
			previewRef.current.style.backgroundImage = 'url(' + URL.createObjectURL(file) + ')'
			videoNode.src = null
		} else {
			const extension = file.name.split('.').pop().toLowerCase()
			if (extension === 'mp4' || extension === 'mov') { // fallback if no file type available
				try {
					previewRef.current.style.backgroundImage = 'none'
					videoNode.src = URL.createObjectURL(file)
					setPlaying(true)
				} catch (error) {
					console.error(error);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	},[file])

	// open file select dialogue
	function openFileDialog() {
		fileInputRef.current.click()
	}

	// validate file when added
	function onFileAdded(e) {
		validateFile(e.target.files[0])
	}

	// handle dropped file
	function onDrop(e) {
		e.preventDefault()
		setHighlight(false)
		validateFile(e.dataTransfer.files[0])
	}

	// handle drag enter
	function onDragOver(e) {
		e.preventDefault()
		setHighlight(true)
		setError(null)
	}

	// handle drag leave
	function onDragLeave() {
		setHighlight(false)
	}

	// validate file
	function validateFile(file) {
		if (file === null || file === undefined) return
		let err = null
		setError(null)
		if (file.size > maxMb * 1000 * 1000) { // check if file size exceeds subscription
			err = 'Sorry, the video size exceeds the allowed ' + maxMb + ' MB per file'
			setError(err)
			setFile(null)
			props.fileAdded(null)
		} else if (file.type !== 'video/mp4' && file.type !== 'video/quicktime' && file.type.indexOf('image/') !== 0) { // check file type
			err = 'Sorry, flowsam only likes .mp4 or .mov video or image files'
			setError(err)
			setFile(null)
			props.fileAdded(null)
		} else if (file.type.indexOf('image/') === 0) { // check image dimensions and aspect ratio
			const img = new Image()
			img.src = window.URL.createObjectURL(file)
			img.onload = () => {
				err = (img.width && (img.width/img.height < .4 || img.width/img.height > 2.5)) ? 'Sorry, flowsam currently only supports image aspect ratios between 0.4 and 2.5. Your image has an aspect ratio of ' + Math.floor(img.width/img.height*100)/100 + '.' : err
				err = (img.width && (img.width < 160 || img.width > 6000 || img.height < 160 || img.height > 6000)) ? 'Sorry, flowsam currently only supports image resolutions between 160 and 6000' : err
				setError(err ? err : null)
				setFile(err ? null : file)
				props.fileAdded(err ? null : file)
			}
		} else if (file.type.indexOf('video/') === 0) { // check video dimensions and aspect ratio
			const vid = document.createElement('video')
			vid.preload = 'metadata'
			vid.src = window.URL.createObjectURL(file)
			vid.addEventListener('loadedmetadata', function removeVideo() {
				vid.removeEventListener('loadedmetadata', removeVideo)
				vid.videoWidth && clearTimeout(timeoutID)
				err = (vid.videoWidth && (vid.videoWidth/vid.videoHeight < .4 || vid.videoWidth/vid.videoHeight > 2.5)) ? 'Sorry, flowsam currently only supports video aspect ratios between 0.4 and 2.5. Your video has an aspect ratio of ' + Math.floor(vid.videoWidth/vid.videoHeight*100)/100 + '.' : err
				err = (vid.videoWidth && (vid.videoWidth < 160 || vid.videoWidth > 6000 || vid.videoHeight < 160 || vid.videoHeight > 6000)) ? 'Sorry, flowsam currently only supports video resolutions between 160 and 6000' : err
				if (!err && (vid.videoWidth >= 3840 || vid.videoHeight >= 3840)) {
					showAlert(vid, file)
				} else {
					vid.src = ''
					vid.load()
					setError(err ? err : null)
					setFile(err ? null : file)
					props.fileAdded(err ? null : file)
				}
			})
			const timeoutID = setTimeout(function() { // fallback if no metadata
				setError('')
				setFile(file)
				props.fileAdded(file)
				vid.src = ''
				vid.load()
			}, 1000)
		}
	}

	// show alert
	function showAlert(vid, file) {
		setAlertTask(()=>(action)=>{ // define alert action and display alert
			if (action) {
				// do nothing, let user select another video
			} else {
				vid.src = ''
				vid.load()
				setFile(file)
				props.fileAdded(file)
			}
			setAlertTask() // remove alert
		})
	}

	// toggle pause/play
	function togglePlay() {
		var videoNode = document.querySelector('video')
		playing ? videoNode.pause() : videoNode.play()
		setPlaying(!playing)
	}

	const dropZoneStyle = { backgroundColor: (highlight ? colors.dropZoneHighlight : file ? 'transparent' : colors.background1) }
	const statusStyle = { pointerEvents:'none', color: error && !highlight && colors.error }
	const ctaText = isTouchDevice ? 'Tap to select video or image' : 'Drop video or image here\nor click to select'
	const statusText = highlight ? 'Drop it right here' : error ? error : file ? '' /*file.name*/ : (
		<>
			<div className={styles.icon} />
			<p className={styles.newCta}>{ctaText}</p>
		</>
	)
	const controls = file?.type.indexOf('video/') === 0 && <div className={styles.controls + ' ' + (!playing ? styles.paused : '')} onClick={togglePlay}></div>

	return (
		<>
			<div ref={previewRef} className={styles.preview}>
				<video className={styles.previewVideo} loop autoPlay muted playsInline />
			</div>
			<div
				className={styles.uploadField}
				style={dropZoneStyle}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDrop={onDrop}
				onClick={openFileDialog}>
				<input
					type="file"
					ref={fileInputRef}
					style={{display:'none'}}
					onChange={onFileAdded}
					accept="video/mp4,video/quicktime,image/*"
				/>
				<h4 style={statusStyle}>{statusText}</h4>
			</div>
			{controls}
			{alert}
		</>
	)
}
